import { useState, useEffect, lazy, Suspense } from "react";
import React from "react";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import {
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Typography,
  CircularProgress,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useAuth } from "./AuthContext";
import PlayerCardRow from "./PlayerCardRow";
import ProfileTags from "./ProfileTags";
import InfoCard from "./InfoCard";
import StatCard from "./StatCard";
import ArsenalProfile from "./ArsenalProfile";
import CountChart from "./CountChart";
import HitterPitchProfile from "./HitterPitchProfile";
import Heatmap from "./Heatmap";
import BattedBallProfile from "./BattedBallProfile";
import SprayChart from "./SprayChart";
import EbayCards from "./EbayCards";
import RollingGraphs from "./RollingGraphs";
import { KEY } from "./utils/ChartUtils";
import LazyRender from "./utils/LazyRender";
import Strider from "./images/Strider.png";
import Ohtani from "./images/Ohtani.png";

const MovementProfile = lazy(() => import("./MovementProfile"));
const ReleaseProfile = lazy(() => import("./ReleaseProfile"));
const PitchHeatmap = lazy(() => import("./PitchHeatmap"));
const PitchSplits = lazy(() => import("./PitchSplits"));

const teamColors: { [id: string]: string[] } = {
  TBR: ["#02285C", "#90BDE7", "#FFFFFF"],
  BAL: ["#F25D23", "#000000", "#FFFFFF"],
  NYY: ["#132448", "#FFFFFF", "#FFFFFF"],
  BOS: ["#BD3039", "#0D2B56", "#FFFFFF"],
  TOR: ["#134A8E", "#E8291C", "#FFFFFF"],
  CLE: ["#E31937", "#002B5C", "#FFFFFF"],
  KCR: ["#004687", "#FFFFFF", "#FFFFFF"],
  DET: ["#0C2C56", "#FFFFFF", "#FFFFFF"],
  CHW: ["#000000", "#BFBFBF", "#FFFFFF"],
  MIN: ["#091F40", "#E31E34", "#FFFFFF"],
  HOU: ["#002D62", "#F4911E", "#FFFFFF"],
  SEA: ["#005C5C", "#0C2C56", "#FFFFFF"],
  TEX: ["#C0111F", "#003278", "#FFFFFF"],
  LAA: ["#BA0021", "#003263", "#C4CED4"],
  ATH: ["#00342C", "#FFB400", "#FFFFFF"],
  WSN: ["#AB0003", "#FFFFFF", "#FFFFFF"],
  NYM: ["#F75500", "#002C6E", "#FFFFFF"],
  MIA: ["#019CD6", "#E51829", "#FFFFFF"],
  PHI: ["#C21D39", "#01396F", "#FFFFFF"],
  ATL: ["#D3042B", "#001D3D", "#FFFFFF"],
  CIN: ["#C6011F", "#000000", "#FFFFFF"],
  PIT: ["#F7BF02", "#000000", "#FFFFFF"],
  CHC: ["#123581", "#E62F2E", "#FFFFFF"],
  MIL: ["#18294C", "#FEC430", "#FFFFFF"],
  STL: ["#BE0A15", "#FDDB00", "#FFFFFF"],
  LAD: ["#005A9D", "#FFFFFF", "#FFFFFF"],
  ARI: ["#C71128", "#00C2CE", "#FFFFFF"],
  SDP: ["#2D221C", "#FFC125", "#FFFFFF"],
  SFG: ["#231F20", "#F4793E", "#E8D8C4"],
  COL: ["#503E80", "#BFC2C9", "#FFFFFF"],
  FA: ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
};

// A failed FanGraphs player-info lookup leaves MLB_AbbName as the string "0",
// which is truthy but has no palette -- blank it so the FA fallbacks apply.
const withKnownTeam = (row: any) =>
  row && !teamColors[row.MLB_AbbName] ? { ...row, MLB_AbbName: "" } : row;

// Sentinels written when a FanGraphs lookup failed.
const realValue = (v: any) =>
  v === null || v === undefined || v === 0 || v === "0" || v === "" ? undefined : v;

// Last resort when even the stored bio blob is empty: the row's own columns are
// sourced from the MLB Stats API and cover everything the header needs.
const bioFromRow = (row: any) => {
  const name = row?.name || row?.player_name;
  if (!name) return null;
  return {
    firstLastName: name,
    Position: realValue(row.Position) ?? "",
    Bats: realValue(row.Bats) ?? "",
    Throws: realValue(row.Throws) ?? "",
    AgeDisplay:
      realValue(row.AgeDisplayOld) ??
      (realValue(row.age) !== undefined ? String(row.age) : ""),
  };
};

// FanGraphs is only needed for the live-only extras (prospect ranks and draft
// details). The bio/team blobs behind everything else on this page are stored
// next to our own stats, so rebuild the same {common: {playerInfo, teamInfo}}
// shape from the row whenever the live page can't be read.
const infoFromRows = (rows: any, currentKey: string, level: string) => {
  if (!rows) return null;
  const keys = [
    currentKey,
    ...Object.keys(rows).filter((k) => k !== currentKey).sort().reverse(),
  ];
  const row = rows[currentKey];
  const bio =
    keys
      .map((k) => rows[k]?.player_info)
      .find((b: any) => b && typeof b === "object" && b.firstLastName) ??
    bioFromRow(row);
  if (!bio) return null;

  const stored = row?.team_info;
  // The row's own team columns come from the MLB Stats API and are keyed on
  // MLBAM id, so they beat the blob (whatever org FanGraphs listed at scrape).
  const teamInfo = teamColors[row?.MLB_AbbName]
    ? {
      MLB_FullName: row.MLB_FullName,
      MLB_ShortName: row.MLB_ShortName,
      MLB_AbbName: row.MLB_AbbName,
      llevel: level,
    }
    : (stored && typeof stored === "object" ? stored : {});

  return {
    common: { playerInfo: bio, teamInfo, prospect: [] },
    contract: { serviceTime: bio.servicetime ?? null },
    draft: null,
    fallback: true,
  };
};

const mergePlayerInfo = (live: any, rows: any, currentKey: string, level: string) =>
  live && live.common
    ? live
    : (infoFromRows(rows, currentKey, level) ?? live ?? {});

function reducePitchData(data: any[]) {
  const resultMap = new Map();

  data.forEach((item: any) => {
    // Use pitch_type as the key since we want one entry per pitch type
    const key = item.pitch_type;

    if (!resultMap.has(key)) {
      // Initialize with both left and right percentages set to 0
      resultMap.set(key, {
        pitchName: item.pitch_name,
        pitchType: item.pitch_type,
        leftPercent: 0,
        rightPercent: 0
      });
    }

    // Update the appropriate percentage based on batter handedness
    const pitchData = resultMap.get(key);
    if (item.stand === 'L') {
      pitchData.leftPercent = -Math.abs(item.percentage); // Ensure negative for lefties
    } else {
      pitchData.rightPercent = Math.abs(item.percentage); // Ensure positive for righties
    }
  });

  return Array.from(resultMap.values());
}

export default function PlayerPage(props: any) {
  const { user } = useAuth();
  const isLocal = /^(localhost|127\.0\.0\.1|192\.168\.|10\.)/.test(window.location.hostname);
  const isPlus = isLocal || !!user?.is_plus;
  const plusHref = (href: string) => isPlus ? href : "/plus";
  const [playerData, setPlayerData] = useState<any>({});
  const [percentiles, setPercentiles] = useState<any>(null);
  const [playerInfo, setPlayerInfo] = useState<any>({});
  const [loadingPlayer, setLoadingPlayer] = useState(true);
  const { id } = useParams();
  const [seasonLevel, setSeasonLevel] = React.useState("2026_AAA");
  const [seasonLevels, setSeasonLevels] = React.useState<string[]>([]);
  const [seasonStats, setSeasonStats] = useState<any>({});
  const [pitchData, setPitchData] = useState<any[]>([]);
  const [pitches, setPitches] = useState<any[]>([]);
  const [armAngle, setArmAngle] = useState<Number>(0);
  const [splitData, setSplitData] = useState<any[]>([]);
  const [compData, setCompData] = useState<any>(null);
  const [contextData, setContextData] = useState<any>(null);
  const [hitterPitchData, setHitterPitchData] = useState<any[]>([]);

  // Extract season and level from combined state
  const season = seasonLevel.split("_")[0];
  const level = seasonLevel.split("_")[1];

  // AA and High-A are aggregate (non-Statcast) levels: hide Statcast cards/viz
  // and show the aggregate stat layout instead. At Single-A, the California /
  // Carolina leagues (aball_src === "agg") are aggregate too -- same as AA/A+,
  // including zone discipline -- while the Florida State League
  // (aball_src === "fsl") keeps its full Statcast layout.
  const isABallAgg = level === "A" && playerData?.aball_src === "agg";
  // Rookie ball (Complex + DSL) uses the aggregate base layout. The Complex
  // tier additionally carries PARTIAL Statcast (real EV / barrels) harvested
  // from the game-feed crawl, surfaced as extra rows when present. Expected
  // stats (xwOBA/xBA/xSLG) are NOT computable at Rookie level.
  const isRookie = level === "Rk";
  const isAggLevel = level === "AA" || level === "A+" || isABallAgg || isRookie;

  // PS Plus tool availability by level. The DSL (rk_src === "dsl") has no pitch
  // tracking at all, so none of the per-player Plus tools have data there --
  // hide all of them. Every other level has at least zone discipline, which
  // powers MLB Context, MLB Comps and (handedness) Splits.
  const isDSL = isRookie && playerData?.rk_src === "dsl";
  const hasContextTool = !isDSL;
  const hasCompsTool = !isDSL; // hitters-only filter is applied where used
  const hasSplitsTool = !isDSL;

  const handleSeasonLevel = (event: SelectChangeEvent) => {
    setSeasonLevel(event.target.value as string);
  };

  const [qualifier, setQualifier] = React.useState("1");

  const handleQualifier = (event: SelectChangeEvent) => {
    setQualifier(event.target.value as string);
  };
  //console.log(season);

  const [ageRange, setAgeRange] = React.useState<number[]>([16, 40]);

  const handleAgeRange = (event: Event, newValue: any) => {
    setAgeRange(newValue);
  };

  useEffect(() => {
    setLoadingPlayer(true);
    fetch(
      props.prod
        ? "https://oriolebird.pythonanywhere.com/player/" + id
        : "/player/" + id
    )
      .then((res) => res.json())
      .then((data1) => {
        //console.log("D1: ", data1);
        let position: string = "P"

        // Get all available season_level combinations
        const availableSeasonLevels = Object.keys(data1).reverse();
        setSeasonLevels(availableSeasonLevels);

        // Determine which seasonLevel to use
        const currentSeasonLevel = availableSeasonLevels.includes(seasonLevel)
          ? seasonLevel
          : availableSeasonLevels[0];

        const [currentSeason, currentLevel] = currentSeasonLevel.split("_");

        if (availableSeasonLevels.includes(seasonLevel)) {
          setPlayerData(withKnownTeam(data1[seasonLevel]));
          fetch(
            props.prod
              ? `https://oriolebird.pythonanywhere.com/player-info/`
              : "/player-info/",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                playerUrlProp: data1[availableSeasonLevels[0]]["UPURL"],
              }),
            }
          )
            .then((res) => res.json())
            .catch(() => ({}))
            .then((data2: any) => {
              const info = mergePlayerInfo(data2, data1, currentSeasonLevel, currentLevel);
              setPlayerInfo(info);
              //console.log("All Seasons 1", data2)
              const height = info?.common?.playerInfo?.Height ?? 76
              if (Object.keys(data2).includes('stats') && data2.stats.data.length > 0) {
                //console.log("NewData: ",season, data2.stats.data.filter((data: any) => data.sortSeason.toString() === season && data.AbbLevel === level))
                if (data2.stats.data.filter((data: any) => data.sortSeason.toString() === currentSeason && data.AbbLevel === "MiLB" && data.AbbName === '---').length > 0) {
                  //console.log("yarp")
                  setSeasonStats(data2.stats.data.filter((data: any) => data.sortSeason.toString() === currentSeason && data.AbbLevel === "MiLB" && data.AbbName === '---')[0])
                }
                else
                  setSeasonStats(data2.stats.data.filter((data: any) => data.sortSeason.toString() === currentSeason && data.AbbLevel === currentLevel)[0])
                //console.log("D2: ", data2);
              }
              else setSeasonStats(undefined)
              if (position === "P") {
                //console.log(data2)
                fetch(props.prod ? "https://oriolebird.pythonanywhere.com/pitches/" + id + '/' + currentSeason + '/' + height : "/pitches/" + id + '/' + currentSeason + '/' + height)
                  .then((res) => res.json())
                  .then((pData) => {
                    //console.log("Pitch Data for ", season, pData);
                    setPitchData(pData)
                    const p = Object.keys(pData.reduce((r: any, { pitch_type }: any) => { r[pitch_type] = ''; return r; }, {})).map((pitch: string) => KEY[pitch])
                    //console.log("Pitches: ", p.toString())
                    setPitches(p)
                    const spl = reducePitchData(pData)
                    setSplitData(spl)
                    //console.log("Reduced Pitches: ", spl)
                    setArmAngle(pData.length > 0 ? pData[0].avg_arm_angle.toFixed(1) : 0)
                  });
              }
            });
          position =
            data1[availableSeasonLevels[0]].Position === "P" ? "P" : "H";
          fetch(
            props.prod
              ? "https://oriolebird.pythonanywhere.com/get_player_percentiles/" +
              id +
              "/" +
              position +
              "/" +
              currentLevel +
              "/" +
              currentSeason +
              "/" +
              qualifier +
              "/" +
              ageRange[0] +
              "/" +
              ageRange[1]
              : "/get_player_percentiles/" +
              id +
              "/" +
              position +
              "/" +
              currentLevel +
              "/" +
              currentSeason +
              "/" +
              qualifier +
              "/" +
              ageRange[0] +
              "/" +
              ageRange[1]
          )
            .then((res) => res.json())
            .then((data) => {
              //console.log("Fixed P: ", data.data[0]);
              setPercentiles(data.data[0]);
            });
        } else {
          setPlayerData(withKnownTeam(data1[availableSeasonLevels[0]]));
          setSeasonLevel(availableSeasonLevels[0]);
          fetch(
            props.prod
              ? `https://oriolebird.pythonanywhere.com/player-info/`
              : "/player-info/",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                playerUrlProp: data1[availableSeasonLevels[0]]["UPURL"],
              }),
            }
          )
            .then((res) => res.json())
            .catch(() => ({}))
            .then((data2: any) => {
              const info = mergePlayerInfo(data2, data1, currentSeasonLevel, currentLevel);
              setPlayerInfo(info);
              const height = info?.common?.playerInfo?.Height ?? 76;
              //console.log("NewData: ",season, data2.stats.data.filter((data: any) => data.sortSeason.toString() === season && data.AbbLevel === level)[0])
              //console.log("All Seasons 2", data2.stats.data)
              // Players without a FanGraphs page (e.g. many DSL/Rookie players)
              // return {"Error": "No data found"} with no stats payload.
              if (data2 && data2.stats && Array.isArray(data2.stats.data)) {
                setSeasonStats(data2.stats.data.filter((data: any) => data.sortSeason.toString() === currentSeason && data.AbbLevel === currentLevel)[0])
              }
              else setSeasonStats(undefined)
              //console.log("D2: ", data2);
              if (position === "P") {
                //console.log(data2)
                fetch(props.prod ? "https://oriolebird.pythonanywhere.com/pitches/" + id + '/' + currentSeason + '/' + height : "/pitches/" + id + '/' + currentSeason + '/' + height)
                  .then((res) => res.json())
                  .then((pData) => {
                    //console.log("Pitch Data for ", season, pData);
                    setPitchData(pData)
                    const p = Object.keys(pData.reduce((r: any, { pitch_type }: any) => { r[pitch_type] = ''; return r; }, {})).map((pitch: string) => KEY[pitch])
                    //console.log("Pitches: ", p.toString())
                    setPitches(p)
                    const spl = reducePitchData(pData)
                    setSplitData(spl)
                    //console.log("Reduced Pitches: ", spl)
                    setArmAngle(pData.length > 0 ? pData[0].avg_arm_angle.toFixed(1) : 0)
                  });
              }
            });
          position =
            data1[availableSeasonLevels[0]].Position === "P" ? "P" : "H";
          fetch(
            props.prod
              ? "https://oriolebird.pythonanywhere.com/get_player_percentiles/" +
              id +
              "/" +
              position +
              "/" +
              currentLevel +
              "/" +
              currentSeason +
              "/" +
              qualifier +
              "/" +
              ageRange[0] +
              "/" +
              ageRange[1]
              : "/get_player_percentiles/" +
              id +
              "/" +
              position +
              "/" +
              currentLevel +
              "/" +
              currentSeason +
              "/" +
              qualifier +
              "/" +
              ageRange[0] +
              "/" +
              ageRange[1]
          )
            .then((res) => res.json())
            .then((data) => {
              //console.log("Fixed P: ", data.data[0]);
              setPercentiles(data.data[0]);
            });
        }
        //console.log("P%", data1);

      })
      .then(
        () => setLoadingPlayer(false),
        () => setLoadingPlayer(false)
      );

  }, [id, props.prod, seasonLevel, ageRange, qualifier]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch MLB comp data for hitters (curiosity-gap preview)
  useEffect(() => {
    if (!id || !season || !playerData?.Position || playerData.Position === "P") return;
    setCompData(null);
    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";
    fetch(`${base}/mlb-comparison/${id}/${season}/`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error || !d.categories?.length) return;
        // Card mirrors the original combined comparison — use the "overall" category
        const overall = d.categories.find((c: any) => c.key === "overall") || d.categories[0];
        if (overall?.matches?.length > 0) {
          setCompData({ ...d, comparison_stats: overall.comparison_stats, matches: overall.matches });
        }
      })
      .catch(() => {});
  }, [id, season, playerData?.Position, props.prod]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch MLB context data for curiosity-gap preview (both pitchers and hitters)
  useEffect(() => {
    if (!id || !season || !playerData?.Position) return;
    setContextData(null);
    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";
    fetch(`${base}/mlb-context/${id}/${season}/`)
      .then((r) => r.json())
      .then((d) => { if (!d.error && d.stats?.length > 0) setContextData(d); })
      .catch(() => {});
  }, [id, season, playerData?.Position, props.prod]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch hitter pitch-type performance to power the pitch-type interpretation
  // chips (hitters only, Statcast levels). Mirrors HitterPitchProfile's fetch.
  useEffect(() => {
    if (!id || !season || !level || !playerData?.Position || playerData.Position === "P" || isAggLevel) {
      setHitterPitchData([]);
      return;
    }
    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";
    fetch(`${base}/hitter-pitches/${id}/${season}/${level}`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d)) setHitterPitchData(d); })
      .catch(() => {});
  }, [id, season, level, playerData?.Position, isAggLevel, props.prod]); // eslint-disable-line react-hooks/exhaustive-deps
  //console.log("pi:", playerInfo);
  //console.log(percentiles)
  return (
    <div>

      {loadingPlayer && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress />
        </div>
      )}

      {!loadingPlayer && playerData !== undefined && playerData !== null && (
        <div style={{ marginBottom: "10px" }}>
          <title>{playerData.name === undefined ? "Prospect Savant" : playerData.name + " - Prospect Savant"}</title>
          <Grid
            container
            justifyContent="center"
            alignItems="stretch"
            spacing={2}
            maxWidth="100%"
            sx={{
              // Make every card in a row share the same height on desktop:
              // stretch each grid item, then let its card fill the item.
              "& > .MuiGrid-item": { display: "flex", flexDirection: "column" },
              "& > .MuiGrid-item > *": { flexGrow: 1 },
            }}
          >
            <Grid item maxWidth="90vw">
              <Paper
                sx={{
                  height: "100%",
                  maxWidth: "90vw",
                  width: 500,
                  border: "#293241 solid 2px",
                  backgroundColor: "#FFFFFF",
                  boxShadow:
                    "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
                }}
                square
                variant="outlined"
              >
                {playerInfo.common ? (
                  <InfoCard playerInfoProp={playerInfo} idProp={id} />
                ) : (
                  isAggLevel && playerData.name && (
                    // Aggregate-level players (AA / A+ / Cal-Carolina A /
                    // Rookie) carry no FanGraphs profile. Show a minimal bio
                    // card built from our own data instead of leaving the slot
                    // blank.
                    <div>
                      <div
                        style={{
                          padding: "20px",
                          backgroundColor:
                            playerData.MLB_AbbName && teamColors[playerData.MLB_AbbName]
                              ? teamColors[playerData.MLB_AbbName][0]
                              : teamColors["FA"][0],
                        }}
                      >
                        <Grid container direction="row" alignItems="center" spacing={2}>
                          <Grid item>
                            <Box
                              component="img"
                              src={`https://img.mlbstatic.com/mlb-photos/image/upload/c_fill,g_auto/w_180/v1/people/${id}/headshot/milb/current`}
                              alt={playerData.name}
                              onError={(e: any) => {
                                e.target.onerror = null;
                                e.target.src =
                                  "https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_180,q_auto:best/v1/people/" +
                                  id +
                                  "/headshot/67/current";
                              }}
                              sx={{
                                width: 90,
                                height: 90,
                                borderRadius: "50%",
                                border: "2px solid #FFFFFF",
                                backgroundColor: "#FFFFFF",
                                objectFit: "cover",
                              }}
                            />
                          </Grid>
                          <Grid item>
                            <Typography variant="h4">
                              <span
                                style={{
                                  color:
                                    playerData.MLB_AbbName && teamColors[playerData.MLB_AbbName]
                                      ? teamColors[playerData.MLB_AbbName][1]
                                      : teamColors["FA"][1],
                                  fontWeight: 600,
                                }}
                              >
                                {playerData.name} ⋅ {playerData.Position}
                              </span>
                            </Typography>
                            <Typography variant="h6">
                              <span
                                style={{
                                  color:
                                    playerData.MLB_AbbName && teamColors[playerData.MLB_AbbName]
                                      ? teamColors[playerData.MLB_AbbName][2]
                                      : teamColors["FA"][2],
                                }}
                              >
                                {playerData.MLB_AbbName || "FA"} ({level})
                              </span>
                            </Typography>
                            <Typography variant="subtitle1">
                              <span
                                style={{
                                  color:
                                    playerData.MLB_AbbName && teamColors[playerData.MLB_AbbName]
                                      ? teamColors[playerData.MLB_AbbName][2]
                                      : teamColors["FA"][2],
                                }}
                              >
                                <span style={{ fontWeight: "bold" }}>Age:</span>{" "}
                                {playerData.age != null ? playerData.age : "-"}
                              </span>
                            </Typography>
                          </Grid>
                        </Grid>
                      </div>
                      <div style={{ padding: "20px" }}>
                        <Typography variant="subtitle2" sx={{ color: "rgba(0,0,0,0.6)" }}>
                          Detailed bio and scouting info isn't available for this player
                          .
                        </Typography>
                      </div>
                    </div>
                  )
                )}
              </Paper>
            </Grid>
            <Grid item maxWidth="90vw">
              {percentiles !== null &&
                (isAggLevel
                  ? !isNaN(playerData.score_p)
                  : playerData.xwoba_p !== "undefined" && !isNaN(playerData.xwoba_p)) && (
                  <Paper
                    sx={{
                      maxWidth: "90vw",
                      height: "100%",
                      width: 500,
                      border: "#293241 solid 2px",
                      boxShadow:
                        "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
                    }}
                    square
                    variant="outlined"
                  >
                    {playerData.Position === "P" && (
                      <div>
                        <Grid
                          container
                          direction="row"
                          justifyContent="flex-start"
                          alignItems="center"
                          style={{
                            backgroundColor: playerInfo.common && playerInfo.common.teamInfo !== undefined &&
                              "MLB_AbbName" in playerInfo.common.teamInfo &&
                              playerInfo.common.teamInfo.MLB_AbbName !== null
                              ? teamColors[playerInfo.common.teamInfo.MLB_AbbName][0]
                              : (playerData.MLB_AbbName && teamColors[playerData.MLB_AbbName]
                                ? teamColors[playerData.MLB_AbbName][0]
                                : teamColors["FA"][0]),
                            paddingLeft: "10px",
                            paddingBottom: "10px",
                            marginBottom: "10px",
                            wordWrap: "break-word",
                          }}
                        >
                          <img
                            src={Strider}
                            alt=""
                            style={{
                              marginLeft: "10px",
                              marginTop: "10px",
                              width: props.isDesktop ? "64px" : "36px",
                              maxWidth: "12vw",
                            }}
                          />
                          <FormControl
                            variant="standard"
                            sx={{ paddingLeft: "15px", paddingTop: "8px" }}
                          >
                            <Select
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              value={seasonLevel}
                              label="Season/Level"
                              onChange={handleSeasonLevel}
                              defaultValue="2026_AAA"
                              disableUnderline={true}
                              sx={{
                                height: "2.5rem",
                                color: playerData.MLB_AbbName
                                  ? teamColors[playerData.MLB_AbbName][1]
                                  : teamColors["FA"][1],
                                fontSize: props.isDesktop ? "18pt" : "14pt",
                                fontWeight: "bold",
                                "& .MuiSelect-root": {
                                  borderColor: "white",
                                  color: playerData.MLB_AbbName
                                    ? teamColors[playerData.MLB_AbbName][1]
                                    : teamColors["FA"][1],
                                  borderBottom: "1px solid white",
                                },
                                "& .MuiSvgIcon-root": {
                                  color: playerData.MLB_AbbName
                                    ? teamColors[playerData.MLB_AbbName][1]
                                    : teamColors["FA"][1],
                                },
                              }}
                            >
                              {seasonLevels.map((sl) => (
                                <MenuItem key={sl} value={sl}>{sl.replace("_", " ")}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <span
                            style={{
                              marginLeft: "5px",
                              paddingTop: "8px",
                              fontSize: props.isDesktop ? "17pt" : "11pt",
                              fontWeight: "bold",
                              color: playerData.MLB_AbbName
                                ? teamColors[playerData.MLB_AbbName][1]
                                : teamColors["FA"][1],
                            }}
                          >
                            {" "}
                            Pitching Percentiles
                          </span>
                          {[...(hasContextTool ? [{href: `/mlb-context/${id}`, label: "MLB Context", gated: true}] : []), ...(hasCompsTool && (level === "AAA" || isAggLevel) ? [{href: `/pitcher-comparison/${id}`, label: "Pitch Comps", gated: true}] : []), ...(hasSplitsTool ? [{href: `/plus/splits/${id}`, label: "Splits", gated: true}] : []), ...(playerData.Position === "C" ? [{href: "/plus/framing/", label: "Framing", gated: true}] : [])].map(({href, label, gated}) => (
                            <a
                              key={label}
                              href={gated ? plusHref(href) : href}
                              style={{
                                marginLeft: "8px",
                                padding: "2px 8px",
                                fontSize: "0.72rem",
                                fontWeight: "bold",
                                borderRadius: "12px",
                                border: "1.5px solid " + (playerData.MLB_AbbName ? teamColors[playerData.MLB_AbbName][0] : teamColors["FA"][0]),
                                color: playerData.MLB_AbbName ? teamColors[playerData.MLB_AbbName][0] : teamColors["FA"][0],
                                backgroundColor: "#fff",
                                textDecoration: "none",
                                verticalAlign: "middle",
                                whiteSpace: "nowrap",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              {gated && !isPlus && <LockIcon sx={{ fontSize: "0.7rem" }} />}
                              {label} ↗
                            </a>
                          ))}
                        </Grid>
                        <div>
                          <div
                            style={{ margin: "auto", maxWidth: "fit-content" }}
                          >
                            <FormControl>
                              <InputLabel id="demo-simple-select-label">
                                Min IP
                              </InputLabel>
                              <Select
                                value={qualifier}
                                label="Min IP"
                                onChange={handleQualifier}
                                sx={{ width: "102px" }}
                              >
                                {[1, 5, 10, 20, 30, 40, 50, 60, 80, 100, 120, 150].map((v) => (
                                  <MenuItem key={v} value={v}>
                                    {v}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <span
                              style={{
                                position: "absolute",
                                marginLeft: props.isDesktop ? "25px" : "15px",
                                marginTop: "3px",
                                color: "#5A6465",
                                fontSize: 16,
                              }}
                            >
                              Age Range
                            </span>
                            <Slider
                              getAriaLabel={() => "Age Range"}
                              value={ageRange}
                              onChange={handleAgeRange}
                              valueLabelDisplay="on"
                              min={16}
                              max={40}
                              disableSwap={true}
                              sx={{
                                width: "200px",
                                marginLeft: props.isDesktop ? "30px" : "20px",
                                marginTop: "25px",
                                "& .MuiSlider-valueLabel": {
                                  fontSize: 11,
                                  fontWeight: "bold",
                                  marginTop: "33px",
                                  backgroundColor: "unset",
                                  color: "#FFFFFF",
                                  "&::before": {
                                    display: "none",
                                  },
                                  "& *": {
                                    background: "transparent",
                                    color: "#FFFFFF",
                                  },
                                },
                              }}
                            />
                          </div>
                          <ProfileTags
                            percentiles={percentiles}
                            playerData={playerData}
                            position={playerData.Position}
                            level={level}
                            id={id}
                            isDesktop={props.isDesktop}
                          />
                          <PlayerCardRow
                            stat="PS Score"
                            value={(playerData.score_p * 100).toFixed(2).toString()}
                            percentile={percentiles?.score_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.score_p.toFixed(2)}
                            isDesktop={props.isDesktop}
                          />
                          {!isAggLevel && (
                          <>
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="psStuff+"
                            value={percentiles?.psStuffAgg === 0 || percentiles?.psStuffAgg == null ? "-" : Number(percentiles.psStuffAgg).toFixed(1)}
                            percentile={percentiles?.psStuffAgg_p ? percentiles.psStuffAgg_p * 100 : 0}
                            percentile_raw={percentiles?.psStuffAgg_p ?? 0}
                            isDesktop={props.isDesktop}
                            locked={!isPlus}
                          />
                          </>)}
                          <Typography
                            variant="h6"
                            sx={{
                              marginTop: "-5px",
                              color: "rgba(0, 0, 0, 0.4)",
                              fontWeight: 600,
                              fontSize: "0.5rem",
                              pointerEvents: "none",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              letterSpacing: "0.5em", // tweak this value until it fills ~80%
                            }}
                          >
                            ProspectSavant.com/player/{id}
                          </Typography>
                          {!isAggLevel && (
                            <>
                              <PlayerCardRow
                                stat="xwOBA"
                                value={playerData.xwoba.toFixed(3)}
                                percentile={percentiles?.xwoba_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.xwoba_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="xBA"
                                value={playerData.xba.toFixed(3)}
                                percentile={percentiles?.xba_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.xba_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="xSLG"
                                value={playerData.xslg.toFixed(3)}
                                percentile={percentiles?.xslg_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.xslg_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="EV"
                                value={
                                  playerData.ev + (props.isDesktop ? " mph" : "")
                                }
                                percentile={percentiles?.ev_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.ev_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="Barrel %"
                                value={playerData.barrelbbe + "%"}
                                percentile={
                                  percentiles?.barrelbbe_p.toFixed(2) * 100
                                }
                                percentile_raw={percentiles?.barrelbbe_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="Hard-Hit%"
                                value={playerData.hhrate + "%"}
                                percentile={percentiles?.hhrate_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.hhrate_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              {playerData.release_extension > 0 && (() => {
                                // The extension percentile has lived under a
                                // few different keys / sources over time --
                                // accept any of them so the bar isn't 0.
                                const extRaw = Number(
                                  percentiles?.release_extension_p ??
                                  percentiles?.extension_p ??
                                  percentiles?.ext_p ??
                                  percentiles?.release_ext_p ??
                                  playerData?.release_extension_p ??
                                  playerData?.extension_p ??
                                  0
                                );
                                // Some endpoints return 0-100 already.
                                const extPct = extRaw > 1 ? extRaw : extRaw * 100;
                                return (
                                <>
                                  <PlayerCardRow
                                    stat="Extension"
                                    value={
                                      Number(playerData.release_extension).toFixed(1) +
                                      (props.isDesktop ? " ft" : "")
                                    }
                                    percentile={extPct}
                                    percentile_raw={extRaw > 1 ? extRaw / 100 : extRaw}
                                    isDesktop={props.isDesktop}
                                  />
                                  <Divider variant="middle" />
                                </>
                                );
                              })()}
                            </>
                          )}
                          {isAggLevel && (
                            <>
                              <PlayerCardRow
                                stat="FIP"
                                value={Number(playerData.fip).toFixed(2)}
                                percentile={percentiles?.fip_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.fip_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="K-BB %"
                                value={Number(playerData.kbb).toFixed(1) + "%"}
                                percentile={percentiles?.kbb_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.kbb_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="GB %"
                                value={playerData.gbrate != null ? Number(playerData.gbrate).toFixed(1) + "%" : "-"}
                                percentile={percentiles?.gbrate_p != null ? percentiles.gbrate_p.toFixed(2) * 100 : 0}
                                percentile_raw={percentiles?.gbrate_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="wOBA"
                                value={playerData.woba != null ? Number(playerData.woba).toFixed(3).replace(/^0/, "") : "-"}
                                percentile={percentiles?.woba_p != null ? percentiles.woba_p.toFixed(2) * 100 : 0}
                                percentile_raw={percentiles?.woba_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              {isRookie && playerData.velo > 0 && (
                                <>
                                  <PlayerCardRow
                                    stat="Velo"
                                    value={playerData.velo + (props.isDesktop ? " mph" : "")}
                                    percentile={percentiles?.velo_p ? percentiles.velo_p * 100 : 0}
                                    percentile_raw={percentiles?.velo_p ?? 0}
                                    isDesktop={props.isDesktop}
                                  />
                                  <Divider variant="middle" />
                                </>
                              )}
                              {isRookie && playerData.ev > 0 && (
                                <>
                                  <PlayerCardRow
                                    stat="EV"
                                    value={playerData.ev + (props.isDesktop ? " mph" : "")}
                                    percentile={percentiles?.ev_p ? percentiles.ev_p * 100 : 0}
                                    percentile_raw={percentiles?.ev_p ?? 0}
                                    isDesktop={props.isDesktop}
                                  />
                                  <Divider variant="middle" />
                                  <PlayerCardRow
                                    stat="Hard-Hit%"
                                    value={playerData.hhrate + "%"}
                                    percentile={percentiles?.hhrate_p ? percentiles.hhrate_p * 100 : 0}
                                    percentile_raw={percentiles?.hhrate_p ?? 0}
                                    isDesktop={props.isDesktop}
                                  />
                                  <Divider variant="middle" />
                                </>
                              )}
                            </>
                          )}
                          <PlayerCardRow
                            stat="K %"
                            value={Number(playerData.krate).toFixed(1) + "%"}
                            percentile={percentiles?.krate_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.krate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="BB %"
                            value={Number(playerData.bbrate).toFixed(1) + "%"}
                            percentile={percentiles?.bbrate_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.bbrate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Chase %"
                            value={Number(playerData.chaserate).toFixed(2) + "%"}
                            percentile={
                              percentiles?.chaserate_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.chaserate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Whiff %"
                            value={Number(playerData.whiffrate).toFixed(1) + "%"}
                            percentile={
                              percentiles?.whiffrate_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.whiffrate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          {(level === "AAA" || isAggLevel) && (
                            <PlayerCardRow
                              stat="Z-Swing %"
                              value={Number(playerData.zswing).toFixed(2) + "%"}
                              percentile={
                                percentiles?.zswing_p.toFixed(2) * 100
                              }
                              percentile_raw={percentiles?.zswing_p}
                              isDesktop={props.isDesktop}
                            />
                          )}
                          <Divider variant="middle" />
                          {(level === "AAA" || isAggLevel) && (
                            <PlayerCardRow
                              stat="Z-Contact %"
                              value={
                                Number(playerData.zcontact).toFixed(2) + "%"
                              }
                              percentile={
                                percentiles?.zcontact_p.toFixed(2) * 100
                              }
                              percentile_raw={percentiles?.zcontact_p}
                              isDesktop={props.isDesktop}
                            />
                          )}
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="SwStr %"
                            value={Number(playerData.swstr).toFixed(2) + "%"}
                            percentile={percentiles?.swstr_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.swstr_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          {playerData.strikerate != null && (
                            <>
                              <PlayerCardRow
                                stat="Strike %"
                                value={Number(playerData.strikerate).toFixed(1) + "%"}
                                percentile={
                                  percentiles?.strikerate_p != null
                                    ? percentiles.strikerate_p.toFixed(2) * 100
                                    : 0
                                }
                                percentile_raw={percentiles?.strikerate_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                            </>
                          )}
                          {playerData.zonerate != null && (
                            <>
                              <PlayerCardRow
                                stat="Zone %"
                                value={Number(playerData.zonerate).toFixed(1) + "%"}
                                percentile={
                                  percentiles?.zonerate_p != null
                                    ? percentiles.zonerate_p.toFixed(2) * 100
                                    : 0
                                }
                                percentile_raw={percentiles?.zonerate_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                            </>
                          )}


                        </div>
                      </div>
                    )}
                    {playerData.Position !== "P" && (
                      <div>
                        <Grid
                          container
                          direction="row"
                          justifyContent="flex-start"
                          alignItems="center"
                          style={{
                            backgroundColor: playerInfo.common && playerInfo.common.teamInfo !== undefined &&
                              "MLB_AbbName" in playerInfo.common.teamInfo &&
                              playerInfo.common.teamInfo.MLB_AbbName !== null
                              ? teamColors[playerInfo.common.teamInfo.MLB_AbbName][0]
                              : (playerData.MLB_AbbName && teamColors[playerData.MLB_AbbName]
                                ? teamColors[playerData.MLB_AbbName][0]
                                : teamColors["FA"][0]),
                            paddingLeft: "10px",
                            paddingBottom: "10px",
                            marginBottom: "10px",
                            wordWrap: "break-word",
                          }}
                        >
                          <img
                            src={Ohtani}
                            height={props.isDesktop ? "64px" : "42px"}
                            alt=""
                            style={{ marginLeft: "10px", marginTop: "10px" }}
                          />
                          <FormControl
                            variant="standard"
                            sx={{ paddingLeft: "15px", paddingTop: "12px" }}
                          >
                            <Select
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              value={seasonLevel}
                              label="Season/Level"
                              onChange={handleSeasonLevel}
                              defaultValue="2026_AAA"
                              disableUnderline={true}
                              sx={{
                                height: "2.5rem",
                                color: playerData.MLB_AbbName
                                  ? teamColors[playerData.MLB_AbbName][1]
                                  : teamColors["FA"][1],
                                fontSize: props.isDesktop ? "18pt" : "15pt",
                                fontWeight: "bold",
                                "& .MuiSelect-root": {
                                  borderColor: "white",
                                  color: playerData.MLB_AbbName
                                    ? teamColors[playerData.MLB_AbbName][1]
                                    : teamColors["FA"][1],
                                  borderBottom: "1px solid white",
                                },
                                "& .MuiSvgIcon-root": {
                                  color: playerData.MLB_AbbName
                                    ? teamColors[playerData.MLB_AbbName][1]
                                    : teamColors["FA"][1],
                                },
                              }}
                            >
                              {seasonLevels.map((sl) => (
                                <MenuItem key={sl} value={sl}>{sl.replace("_", " ")}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <span
                            style={{
                              marginLeft: "5px",
                              fontSize: props.isDesktop ? "18pt" : "10pt",
                              fontWeight: "bold",
                              marginTop: "12px",
                              color: playerData.MLB_AbbName
                                ? teamColors[playerData.MLB_AbbName][1]
                                : teamColors["FA"][1],
                            }}
                          >
                            Hitting Percentiles
                          </span>
                          {[...(hasContextTool ? [{href: `/mlb-context/${id}`, label: "MLB Context", gated: true}] : []), ...(hasCompsTool && (level === "AAA" || isAggLevel) ? [{href: `/mlb-comparison/${id}`, label: "MLB Comps", gated: true}] : []), ...(hasSplitsTool ? [{href: `/plus/splits/${id}`, label: "Splits", gated: true}] : []), ...(playerData.Position === "C" ? [{href: "/plus/framing/", label: "Framing", gated: true}] : [])].map(({href, label, gated}) => (
                            <a
                              key={label}
                              href={gated ? plusHref(href) : href}
                              style={{
                                marginLeft: "8px",
                                padding: "2px 8px",
                                fontSize: "0.72rem",
                                fontWeight: "bold",
                                borderRadius: "12px",
                                border: "1.5px solid " + (playerData.MLB_AbbName ? teamColors[playerData.MLB_AbbName][0] : teamColors["FA"][0]),
                                color: playerData.MLB_AbbName ? teamColors[playerData.MLB_AbbName][0] : teamColors["FA"][0],
                                backgroundColor: "#fff",
                                textDecoration: "none",
                                verticalAlign: "middle",
                                whiteSpace: "nowrap",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              {gated && !isPlus && <LockIcon sx={{ fontSize: "0.7rem" }} />}
                              {label} ↗
                            </a>
                          ))}
                        </Grid>
                        <div>
                          <div
                            style={{ margin: "auto", maxWidth: "fit-content" }}
                          >
                            <FormControl>
                              <InputLabel id="demo-simple-select-label">
                                Min PA
                              </InputLabel>
                              <Select
                                value={qualifier}
                                label="Min PA"
                                onChange={handleQualifier}
                                sx={{ width: "102px" }}
                              >
                                {[1, 10, 25, 50, 75, 100, 150, 200, 250, 300, 350, 400, 450, 500].map((v) => (
                                  <MenuItem key={v} value={v}>
                                    {v}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <span
                              style={{
                                position: "absolute",
                                marginLeft: props.isDesktop ? "25px" : "15px",
                                marginTop: "3px",
                                color: "#5A6465",
                                fontSize: 16,
                              }}
                            >
                              Age Range
                            </span>
                            <Slider
                              getAriaLabel={() => "Age Range"}
                              value={ageRange}
                              onChange={handleAgeRange}
                              valueLabelDisplay="on"
                              min={16}
                              max={40}
                              disableSwap={true}
                              sx={{
                                width: "200px",
                                marginLeft: props.isDesktop ? "30px" : "20px",
                                marginTop: "25px",
                                "& .MuiSlider-valueLabel": {
                                  fontSize: 11,
                                  fontWeight: "bold",
                                  marginTop: "33px",
                                  backgroundColor: "unset",
                                  color: "#FFFFFF",
                                  "&::before": {
                                    display: "none",
                                  },
                                  "& *": {
                                    background: "transparent",
                                    color: "#FFFFFF",
                                  },
                                },
                              }}
                            />
                          </div>
                          <ProfileTags
                            percentiles={percentiles}
                            playerData={playerData}
                            position={playerData.Position}
                            level={level}
                            id={id}
                            pitchProfile={hitterPitchData}
                            isDesktop={props.isDesktop}
                          />
                          <PlayerCardRow
                            stat="PS Score"
                            value={(playerData.score_p * 100).toFixed(2).toString()}
                            percentile={percentiles?.score_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.score_p}
                            isDesktop={props.isDesktop}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              marginTop: "-5px",
                              color: "rgba(0, 0, 0, 0.4)",
                              fontWeight: 600,
                              fontSize: "0.5rem",
                              pointerEvents: "none",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              letterSpacing: "0.5em", // tweak this value until it fills ~80%
                            }}
                          >
                            ProspectSavant.com/player/{id}
                          </Typography>
                          {isAggLevel && (
                            <>
                              <PlayerCardRow
                                stat="wOBA"
                                value={Number(playerData.woba).toFixed(3)}
                                percentile={percentiles?.woba_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.woba_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="wOBACON"
                                value={playerData.wobacon != null ? Number(playerData.wobacon).toFixed(3) : "-"}
                                percentile={percentiles?.wobacon_p != null ? percentiles.wobacon_p.toFixed(2) * 100 : 0}
                                percentile_raw={percentiles?.wobacon_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="wRC+"
                                value={Number(playerData.wrcplus).toFixed(0)}
                                percentile={percentiles?.wrcplus_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.wrcplus_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="ISO"
                                value={Number(playerData.iso).toFixed(3)}
                                percentile={percentiles?.iso_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.iso_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="Pull Air %"
                                value={playerData.pullair != null ? Number(playerData.pullair).toFixed(1) + "%" : "-"}
                                percentile={percentiles?.pullair_p != null ? percentiles.pullair_p.toFixed(2) * 100 : 0}
                                percentile_raw={percentiles?.pullair_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="GB %"
                                value={playerData.gbrate != null ? Number(playerData.gbrate).toFixed(1) + "%" : "-"}
                                percentile={percentiles?.gbrate_p != null ? percentiles.gbrate_p.toFixed(2) * 100 : 0}
                                percentile_raw={percentiles?.gbrate_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              {isRookie && playerData.ev > 0 && (
                                <>
                                  <PlayerCardRow
                                    stat="EV"
                                    value={playerData.ev + (props.isDesktop ? " mph" : "")}
                                    percentile={percentiles?.ev_p ? percentiles.ev_p * 100 : 0}
                                    percentile_raw={percentiles?.ev_p ?? 0}
                                    isDesktop={props.isDesktop}
                                  />
                                  <Divider variant="middle" />
                                  <PlayerCardRow
                                    stat="Max EV"
                                    value={playerData.maxev + (props.isDesktop ? " mph" : "")}
                                    percentile={percentiles?.maxev_p ? percentiles.maxev_p * 100 : 0}
                                    percentile_raw={percentiles?.maxev_p ?? 0}
                                    isDesktop={props.isDesktop}
                                  />
                                  <Divider variant="middle" />
                                  <PlayerCardRow
                                    stat="90th% EV"
                                    value={playerData.ev90 + (props.isDesktop ? " mph" : "")}
                                    percentile={percentiles?.ev90_p ? percentiles.ev90_p * 100 : 0}
                                    percentile_raw={percentiles?.ev90_p ?? 0}
                                    isDesktop={props.isDesktop}
                                  />
                                  <Divider variant="middle" />
                                  <PlayerCardRow
                                    stat="Hard-Hit%"
                                    value={playerData.hhrate + "%"}
                                    percentile={percentiles?.hhrate_p ? percentiles.hhrate_p * 100 : 0}
                                    percentile_raw={percentiles?.hhrate_p ?? 0}
                                    isDesktop={props.isDesktop}
                                  />
                                  <Divider variant="middle" />
                                  <PlayerCardRow
                                    stat="Barrel %"
                                    value={playerData.barrelbbe + "%"}
                                    percentile={percentiles?.barrelbbe_p ? percentiles.barrelbbe_p * 100 : 0}
                                    percentile_raw={percentiles?.barrelbbe_p ?? 0}
                                    isDesktop={props.isDesktop}
                                  />
                                  <Divider variant="middle" />
                                </>
                              )}
                            </>
                          )}
                          {!isAggLevel && (
                            <>
                              <PlayerCardRow
                                stat="xwOBA"
                                value={playerData.xwoba.toFixed(3)}
                                percentile={percentiles?.xwoba_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.xwoba_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="xBA"
                                value={playerData.xba.toFixed(3)}
                                percentile={percentiles?.xba_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.xba_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="xSLG"
                                value={playerData.xslg.toFixed(3)}
                                percentile={percentiles?.xslg_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.xslg_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="EV"
                                value={
                                  playerData.ev + (props.isDesktop ? " mph" : "")
                                }
                                percentile={percentiles?.ev_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.ev_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              {(
                                <PlayerCardRow
                                  stat="Max EV"
                                  value={
                                    playerData.maxev +
                                    (props.isDesktop ? " mph" : "")
                                  }
                                  percentile={percentiles?.maxev_p.toFixed(2) * 100}
                                  percentile_raw={percentiles?.maxev_p}
                                  isDesktop={props.isDesktop}
                                />
                              )}
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="50th% EV"
                                value={
                                  playerData.ev50 + (props.isDesktop ? " mph" : "")
                                }
                                percentile={percentiles?.ev50_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.ev50_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="90th% EV"
                                value={
                                  playerData.ev90 + (props.isDesktop ? " mph" : "")
                                }
                                percentile={percentiles?.ev90_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.ev90_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="Barrel %"
                                value={playerData.barrelbbe + "%"}
                                percentile={
                                  percentiles?.barrelbbe_p.toFixed(2) * 100
                                }
                                percentile_raw={percentiles?.barrelbbe_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                              <PlayerCardRow
                                stat="Hard-Hit%"
                                value={playerData.hhrate + "%"}
                                percentile={percentiles?.hhrate_p.toFixed(2) * 100}
                                percentile_raw={percentiles?.hhrate_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                            </>
                          )}
                          <PlayerCardRow
                            stat="K %"
                            value={Number(playerData.krate).toFixed(1) + "%"}
                            percentile={percentiles?.krate_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.krate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="BB %"
                            value={Number(playerData.bbrate).toFixed(1) + "%"}
                            percentile={percentiles?.bbrate_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.bbrate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Chase %"
                            value={Number(playerData.chaserate).toFixed(2) + "%"}
                            percentile={
                              percentiles?.chaserate_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.chaserate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Whiff %"
                            value={Number(playerData.whiffrate).toFixed(1) + "%"}
                            percentile={
                              percentiles?.whiffrate_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.whiffrate_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          {!isAggLevel && (
                            <>
                              <PlayerCardRow
                                stat="Pull Air %"
                                value={Number(playerData.pullair).toFixed(2) + "%"}
                                percentile={
                                  percentiles?.pullair_p.toFixed(2) * 100
                                }
                                percentile_raw={percentiles?.pullair_p}
                                isDesktop={props.isDesktop}
                              />
                              <Divider variant="middle" />
                            </>
                          )}
                          <PlayerCardRow
                            stat="Swing %"
                            value={Number(playerData.swing).toFixed(2) + "%"}
                            percentile={
                              percentiles?.swing_p.toFixed(2) * 100
                            }
                            percentile_raw={percentiles?.swing_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          {(level === "AAA" || isAggLevel) && (
                            <PlayerCardRow
                              stat="Z-Swing %"
                              value={Number(playerData.zswing).toFixed(2) + "%"}
                              percentile={
                                percentiles?.zswing_p.toFixed(2) * 100
                              }
                              percentile_raw={percentiles?.zswing_p}
                              isDesktop={props.isDesktop}
                            />
                          )}
                          <Divider variant="middle" />
                          {(level === "AAA" || isAggLevel) && (
                            <PlayerCardRow
                              stat="Z-Contact %"
                              value={
                                Number(playerData.zcontact).toFixed(2) + "%"
                              }
                              percentile={
                                percentiles?.zcontact_p.toFixed(2) * 100
                              }
                              percentile_raw={percentiles?.zcontact_p}
                              isDesktop={props.isDesktop}
                            />
                          )}
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="SwStr %"
                            value={Number(playerData.swstr).toFixed(2) + "%"}
                            percentile={percentiles?.swstr_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.swstr_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="Spd"
                            value={Number(playerData.spd).toFixed(2)}
                            percentile={percentiles?.spd_p.toFixed(2) * 100}
                            percentile_raw={percentiles?.spd_p}
                            isDesktop={props.isDesktop}
                          />
                          <Divider variant="middle" />
                          <PlayerCardRow
                            stat="wBsR/PA"
                            value={Number(playerData.wbsr_pa).toFixed(4)}
                            percentile={percentiles?.wbsr_pa_p * 100}
                            percentile_raw={percentiles?.wbsr_pa_p}
                            isDesktop={props.isDesktop}
                          />
                        </div>
                      </div>
                    )}
                  </Paper>
                )}
            </Grid>
            {true && (
              <Grid item maxWidth="90vw">
                <Paper
                  sx={{
                    maxWidth: "90vw",
                    height: "100%",
                    width: 500,
                    border: "#293241 solid 2px",
                    boxShadow:
                      "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
                  }}
                  square
                  variant="outlined"
                >
                  {(playerInfo.common || isAggLevel) && (
                    <StatCard
                      playerDataProp={playerData}
                      playerInfoProp={playerInfo}
                      isDesktop={props.isDesktop}
                      season={season}
                      seasonStatProp={seasonStats}
                      level={level}
                    />
                  )}
                </Paper>
              </Grid>
            )}
            {playerData.Position === "P" && !isAggLevel && "common" in playerInfo && (
              <Grid item maxWidth="90vw">
                <ArsenalProfile
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  isPlus={isPlus}
                />
              </Grid>
            )}
            {playerData.Position === "P" && !isAggLevel && "common" in playerInfo && (
              <LazyRender>
                <Suspense fallback={
                  <Grid item maxWidth="90vw">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                }>
                  <MovementProfile
                    isDesktop={props.isDesktop}
                    season={season}
                    id={id}
                    prod={props.prod}
                    height={playerInfo.common.playerInfo.Height ?? 76}
                    playerInfoProp={playerInfo}
                    pitchData={pitchData}
                    pitches={pitches}
                    playerDataProp={playerData}
                  />
                </Suspense>
              </LazyRender>
            )}
            {playerData.Position === "P" && !isAggLevel && "common" in playerInfo && (
              <LazyRender>
                <Suspense fallback={
                  <Grid item maxWidth="90vw">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                }>
                  <PitchHeatmap
                    isDesktop={props.isDesktop}
                    season={season}
                    id={id}
                    prod={props.prod}
                    playerInfoProp={playerInfo}
                    pitchData={pitchData}
                    pitches={pitches}
                    playerDataProp={playerData}
                  />
                </Suspense>
              </LazyRender>
            )}
            {playerData.Position === "P" && !isAggLevel && "common" in playerInfo && (
              <LazyRender>
                <Suspense fallback={
                  <Grid item maxWidth="90vw">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                }>
                  <ReleaseProfile
                    isDesktop={props.isDesktop}
                    season={season}
                    id={id}
                    prod={props.prod}
                    height={playerInfo.common.playerInfo.Height ?? 76}
                    playerInfoProp={playerInfo}
                    pitchData={pitchData}
                    pitches={pitches}
                    playerDataProp={playerData}
                    armAngle={armAngle}
                  />
                </Suspense>
              </LazyRender>
            )}
            {playerData.Position === "P" && !isAggLevel && "common" in playerInfo && (
              <LazyRender>
                <Suspense fallback={
                  <Grid item maxWidth="90vw">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                }>
                  <PitchSplits
                    isDesktop={props.isDesktop}
                    season={season}
                    id={id}
                    prod={props.prod}
                    height={playerInfo.common.playerInfo.Height ?? 76}
                    playerInfoProp={playerInfo}
                    splitData={splitData}
                    playerDataProp={playerData}
                  />
                </Suspense>
              </LazyRender>
            )}
            {playerData.Position === "P" && !isAggLevel && pitchData.length > 0 && (
              <CountChart
                pitchData={pitchData}
                isDesktop={props.isDesktop}
                playerDataProp={playerData}
                pitches={pitches}
              />
            )}
            {playerData.Position !== "P" && !isAggLevel && "common" in playerInfo && (
              <Grid item maxWidth="90vw" id="pitch-type-performance" sx={{ scrollMarginTop: "80px" }}>
                <HitterPitchProfile
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  level={level}
                  isPlus={isPlus}
                />
              </Grid>
            )}
            {playerData.Position !== "P" && !isAggLevel && "common" in playerInfo && (
              <Grid item maxWidth="90vw">
                <Heatmap
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  level={level}
                />
              </Grid>
            )}
            {playerData.Position !== "P" && !isAggLevel && "common" in playerInfo && (
              <Grid item maxWidth="90vw">
                <BattedBallProfile
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  playerData={playerData}
                />
              </Grid>
            )}
            {playerData.Position !== "P" && !isAggLevel && "common" in playerInfo && (
              <Grid item maxWidth="90vw">
                <SprayChart
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  playerData={playerData}
                />
              </Grid>
            )}
            {"common" in playerInfo && !isAggLevel && (
              <Grid item maxWidth="90vw">
                <RollingGraphs
                  isDesktop={props.isDesktop}
                  season={season}
                  id={id}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  playerData={playerData}
                  type={playerData.Position === "P" ? "pitcher" : "batter"}
                />
              </Grid>
            )}
            {("common" in playerInfo || isAggLevel) && compData && playerData.Position !== "P" && (
              <Grid item maxWidth="90vw">
                <Paper
                  square
                  variant="outlined"
                  sx={{
                    width: 500,
                    maxWidth: "90vw",
                    border: "#293241 solid 2px",
                    boxShadow: "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
                    overflow: "hidden",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  {/* Header — matches team-colored card headers */}
                  <div
                    style={{
                      width: "100%",
                      paddingTop: "10px",
                      paddingBottom: "10px",
                      backgroundColor:
                        playerInfo.common?.teamInfo?.MLB_AbbName
                          ? teamColors[playerInfo.common.teamInfo.MLB_AbbName][0]
                          : teamColors["FA"][0],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingLeft: "10px",
                      paddingRight: "12px",
                      boxSizing: "border-box",
                    }}
                  >
                    <span
                      style={{
                        color: playerInfo.common?.teamInfo?.MLB_AbbName
                          ? teamColors[playerInfo.common.teamInfo.MLB_AbbName][1]
                          : teamColors["FA"][1],
                        fontWeight: "bold",
                        fontSize: props.isDesktop ? "25pt" : "17pt",
                      }}
                    >
                      MLB Comps
                    </span>
                    <a
                      href={plusHref(`/mlb-comparison/${id}`)}
                      style={{
                        color: playerInfo.common?.teamInfo?.MLB_AbbName
                          ? teamColors[playerInfo.common.teamInfo.MLB_AbbName][1]
                          : teamColors["FA"][1],
                        fontSize: "0.75rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        textDecoration: "none",
                        opacity: 0.9,
                        fontWeight: "bold",
                      }}
                    >
                      {!isPlus && <LockIcon sx={{ fontSize: "0.75rem" }} />} Full Report ↗
                    </a>
                  </div>

                  {/* Overall comp — top 3 comps; names blurred for free (top) */}
                  <Box>
                    <TableContainer sx={{ overflowX: "auto" }}>
                      <Table size="small" sx={{ tableLayout: "auto" }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#293241" }}>
                            <TableCell sx={{ color: "white", fontWeight: "bold", minWidth: 130, py: 1, fontSize: "0.75rem" }}>Player</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "center", width: 45, py: 1, fontSize: "0.75rem" }}>PA</TableCell>
                            <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "center", width: 55, py: 1, fontSize: "0.75rem" }}>Match</TableCell>
                            {compData.comparison_stats.map((s: any) => (
                              <TableCell key={s.label} sx={{ color: "white", fontWeight: "bold", textAlign: "center", py: 1, fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                                {s.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {/* Prospect row */}
                          <TableRow sx={{ backgroundColor: "#3D5A80" }}>
                            <TableCell sx={{ py: 0.75 }}>
                              <Typography variant="body2" fontWeight="bold" color="white" noWrap>{compData.name}</Typography>
                              <Typography variant="caption" sx={{ color: "#ABB8C8" }}>{compData.level} {compData.season}</Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", color: "white", fontWeight: "bold", fontSize: "0.82rem" }}>{compData.pa ?? "—"}</TableCell>
                            <TableCell sx={{ textAlign: "center", color: "#ABB8C8", fontSize: "0.82rem" }}>—</TableCell>
                            {compData.comparison_stats.map((s: any) => (
                              <TableCell key={s.label} sx={{ textAlign: "center", color: "white", fontWeight: "bold", fontSize: "0.82rem", py: 0.75 }}>
                                {s.value.toFixed(1)}
                              </TableCell>
                            ))}
                          </TableRow>
                          {/* Top 3 overall comps */}
                          {compData.matches.slice(0, 3).map((m: any, i: number) => {
                            const statMap: Record<string, number> = {};
                            m.stats.forEach((s: any) => { statMap[s.label] = s.value; });
                            return (
                              <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? "white" : "#f9f9f9", "&:hover": { backgroundColor: "#e8f0fe" } }}>
                                <TableCell sx={{ py: 0.75 }}>
                                  {isPlus ? (
                                    <>
                                      {m.mlbam_id ? (
                                        <a
                                          href={`https://baseballsavant.mlb.com/savant-player/${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-${m.mlbam_id}`}
                                          target="_blank" rel="noopener noreferrer"
                                          style={{ fontWeight: "bold", color: "#3D5A80", textDecoration: "none", fontSize: "0.82rem" }}
                                        >
                                          {m.name}
                                        </a>
                                      ) : (
                                        <Typography variant="body2" fontWeight="bold" noWrap fontSize="0.82rem">{m.name}</Typography>
                                      )}
                                      <Typography variant="caption" color="text.secondary">{m.team}{m.season ? ` · ${m.season}` : ""}</Typography>
                                    </>
                                  ) : (
                                    <>
                                      <Typography variant="body2" fontWeight="bold" noWrap fontSize="0.82rem" sx={{ filter: "blur(5px)", userSelect: "none", pointerEvents: "none", color: "transparent", textShadow: "0 0 8px #555" }}>MLB Player Name</Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ filter: "blur(4px)", userSelect: "none", pointerEvents: "none", color: "transparent", textShadow: "0 0 6px #999" }}>TM · YYYY</Typography>
                                    </>
                                  )}
                                </TableCell>
                                <TableCell sx={{ textAlign: "center", fontSize: "0.82rem", py: 0.75 }}>{m.pa}</TableCell>
                                <TableCell sx={{ textAlign: "center", py: 0.75 }}>
                                  <Box component="span" sx={{ display: "inline-block", px: 0.75, py: 0.25, borderRadius: 1, fontSize: "0.75rem", fontWeight: "bold", color: "white", backgroundColor: m.similarity >= 65 ? "#1a7c1a" : m.similarity >= 45 ? "#b8860b" : "#888", minWidth: 34, textAlign: "center" }}>
                                    {m.similarity}%
                                  </Box>
                                </TableCell>
                                {compData.comparison_stats.map((s: any) => {
                                  const val = statMap[s.label];
                                  return (
                                    <TableCell key={s.label} sx={{ textAlign: "center", fontSize: "0.82rem", py: 0.75 }}>
                                      {val !== undefined ? val.toFixed(1) : "—"}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {/* Unlock CTA for free users */}
                    {!isPlus && (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 1, px: 1.5, borderTop: "1px solid #e0e0e0", backgroundColor: "#f7f9fa" }}>
                        <Typography variant="caption" sx={{ color: "#607080" }}>Overall comp names hidden</Typography>
                        <a
                          href="/plus"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            backgroundColor: "#EE6C4D",
                            color: "white",
                            padding: "5px 13px",
                            borderRadius: "16px",
                            textDecoration: "none",
                            fontWeight: "bold",
                            fontSize: "0.78rem",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                          }}
                        >
                          <LockIcon sx={{ fontSize: "0.85rem" }} />
                          Unlock · PS Plus
                        </a>
                      </Box>
                    )}
                  </Box>

                  {/* Skill-by-skill comps — the free hook (names + stats shown to everyone) */}
                  {(() => {
                    const order = ["power", "contact", "approach"];
                    const skills = (compData.categories || [])
                      .filter((c: any) => order.includes(c.key) && c.matches?.length > 0)
                      .sort((a: any, b: any) => order.indexOf(a.key) - order.indexOf(b.key));
                    if (!skills.length) return null;
                    return (
                      <Box sx={{ borderTop: "2px solid #293241" }}>
                        {skills.map((c: any) => {
                          const m = c.matches[0];
                          const statMap: Record<string, number> = {};
                          m.stats.forEach((s: any) => { statMap[s.label] = s.value; });
                          const badgeBg = m.similarity >= 65 ? "#1a7c1a" : m.similarity >= 45 ? "#b8860b" : "#888";
                          return (
                            <Box key={c.key} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                              {/* Pill header: skill tag · plays like · comp · match */}
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 0.5, px: 1.5, py: 0.6, backgroundColor: "#f2f6f7" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                                  <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: "12px", backgroundColor: "#293241", color: "white", fontWeight: "bold", fontSize: "0.66rem", letterSpacing: 0.5, textTransform: "uppercase", whiteSpace: "nowrap" }}>
                                    {c.label}
                                  </Box>
                                  <Typography variant="caption" sx={{ color: "#607080", whiteSpace: "nowrap" }}>plays like</Typography>
                                  {m.mlbam_id ? (
                                    <a
                                      href={`https://baseballsavant.mlb.com/savant-player/${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-${m.mlbam_id}`}
                                      target="_blank" rel="noopener noreferrer"
                                      style={{ fontWeight: "bold", color: "#3D5A80", textDecoration: "none", fontSize: "0.85rem", whiteSpace: "nowrap" }}
                                    >
                                      {m.name}
                                    </a>
                                  ) : (
                                    <Typography variant="body2" fontWeight="bold" noWrap sx={{ fontSize: "0.85rem" }}>{m.name}</Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {m.team}{m.season ? ` '${String(m.season).slice(2)}` : ""}
                                  </Typography>
                                </Box>
                                <Box component="span" sx={{ display: "inline-block", px: 0.75, py: 0.2, borderRadius: 1, fontSize: "0.72rem", fontWeight: "bold", color: "white", backgroundColor: badgeBg, minWidth: 34, textAlign: "center" }}>
                                  {m.similarity}%
                                </Box>
                              </Box>
                              {/* Stat comparison: prospect vs skill comp */}
                              <TableContainer sx={{ overflowX: "auto" }}>
                                <Table size="small" sx={{ tableLayout: "auto" }}>
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: "#293241" }}>
                                      <TableCell sx={{ color: "white", fontWeight: "bold", py: 0.5, fontSize: "0.68rem", minWidth: 90 }} />
                                      {c.comparison_stats.map((s: any) => (
                                        <TableCell key={s.label} sx={{ color: "white", fontWeight: "bold", textAlign: "center", py: 0.5, fontSize: "0.68rem", whiteSpace: "nowrap" }}>{s.label}</TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    <TableRow sx={{ backgroundColor: "#3D5A80" }}>
                                      <TableCell sx={{ py: 0.5, color: "white", fontWeight: "bold", fontSize: "0.72rem", whiteSpace: "nowrap" }}>{compData.name}</TableCell>
                                      {c.comparison_stats.map((s: any) => (
                                        <TableCell key={s.label} sx={{ textAlign: "center", color: "white", fontWeight: "bold", fontSize: "0.75rem", py: 0.5 }}>{s.value.toFixed(1)}</TableCell>
                                      ))}
                                    </TableRow>
                                    <TableRow sx={{ backgroundColor: "white" }}>
                                      <TableCell sx={{ py: 0.5, fontWeight: "bold", fontSize: "0.72rem", color: "#3D5A80", whiteSpace: "nowrap" }}>{m.name}</TableCell>
                                      {c.comparison_stats.map((s: any) => {
                                        const val = statMap[s.label];
                                        return (
                                          <TableCell key={s.label} sx={{ textAlign: "center", fontSize: "0.75rem", py: 0.5 }}>{val !== undefined ? val.toFixed(1) : "—"}</TableCell>
                                        );
                                      })}
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })()}
                </Paper>
              </Grid>
            )}
            {("common" in playerInfo || isAggLevel) && contextData && (
              <Grid item maxWidth="90vw">
                <Paper
                  square
                  variant="outlined"
                  sx={{
                    width: 500,
                    maxWidth: "90vw",
                    border: "#293241 solid 2px",
                    boxShadow: "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
                    overflow: "hidden",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  {/* Header — team-colored, matches other cards */}
                  <div
                    style={{
                      width: "100%",
                      paddingTop: "10px",
                      paddingBottom: "10px",
                      backgroundColor:
                        playerInfo.common?.teamInfo?.MLB_AbbName
                          ? teamColors[playerInfo.common.teamInfo.MLB_AbbName][0]
                          : teamColors["FA"][0],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingLeft: "10px",
                      paddingRight: "12px",
                      boxSizing: "border-box",
                    }}
                  >
                    <span
                      style={{
                        color: playerInfo.common?.teamInfo?.MLB_AbbName
                          ? teamColors[playerInfo.common.teamInfo.MLB_AbbName][1]
                          : teamColors["FA"][1],
                        fontWeight: "bold",
                        fontSize: props.isDesktop ? "25pt" : "17pt",
                      }}
                    >
                      MLB Context
                    </span>
                    <a
                      href={plusHref(`/mlb-context/${id}`)}
                      style={{
                        color: playerInfo.common?.teamInfo?.MLB_AbbName
                          ? teamColors[playerInfo.common.teamInfo.MLB_AbbName][1]
                          : teamColors["FA"][1],
                        fontSize: "0.75rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        textDecoration: "none",
                        opacity: 0.9,
                        fontWeight: "bold",
                      }}
                    >
                      {!isPlus && <LockIcon sx={{ fontSize: "0.75rem" }} />} Full Report ↗
                    </a>
                  </div>

                  {/* Subtitle */}
                  <Box sx={{ backgroundColor: "#3D5A80", px: 2, py: 0.75, textAlign: "center" }}>
                    <Typography variant="caption" color="#ABB8C8">
                      MLB Equivalent Percentiles — {contextData.level} {contextData.season}
                    </Typography>
                  </Box>

                  {/* Rows */}
                  <Box sx={{ position: "relative" }}>
                    {isPlus ? (
                      // Real data for Plus subscribers
                      contextData.stats.map((s: any, i: number) => (
                        <React.Fragment key={s.label}>
                          <PlayerCardRow
                            stat={s.label}
                            value={
                              ["xwOBA", "xBA", "xSLG"].includes(s.label)
                                ? s.value.toFixed(3)
                                : ["EV", "Max EV"].includes(s.label)
                                ? s.value.toFixed(1)
                                : s.value.toFixed(1) + "%"
                            }
                            percentile={Math.round(s.mlb_p * 100)}
                            percentile_raw={s.mlb_p}
                            isDesktop={props.isDesktop}
                          />
                          {i < contextData.stats.length - 1 && <Divider variant="middle" />}
                        </React.Fragment>
                      ))
                    ) : (
                      // Fake placeholder rows for non-Plus
                      <>
                        {contextData.stats.map((_s: any, i: number) => (
                          <React.Fragment key={i}>
                            <Box
                              sx={{
                                filter: "blur(8px)",
                                userSelect: "none",
                                pointerEvents: "none",
                              }}
                            >
                              <PlayerCardRow
                                stat={["xwOBA", "K%", "BB%", "EV", "Barrel%", "Chase%", "Whiff%", "xBA", "Hard-Hit%", "SwStr%"][i % 10]}
                                value={["xwOBA", "xBA", "xSLG"].includes(["xwOBA", "K%", "BB%", "EV", "Barrel%", "Chase%", "Whiff%", "xBA", "Hard-Hit%", "SwStr%"][i % 10]) ? "." + (310 + i * 17) : (18 + i * 7) + "%"}
                                percentile={45 + (i * 13) % 50}
                                percentile_raw={(45 + (i * 13) % 50) / 100}
                                isDesktop={props.isDesktop}
                              />
                            </Box>
                            {i < contextData.stats.length - 1 && <Divider variant="middle" />}
                          </React.Fragment>
                        ))}

                        {/* Gradient + lock overlay */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.75) 35%, rgba(255,255,255,0.97) 65%, #ffffff 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                          }}
                        >
                          <a
                            href="/plus"
                            style={{
                              pointerEvents: "auto",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              backgroundColor: "#293241",
                              color: "white",
                              padding: "6px 14px",
                              borderRadius: "16px",
                              textDecoration: "none",
                              fontWeight: "bold",
                              fontSize: "0.82rem",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                            }}
                          >
                            <LockIcon sx={{ fontSize: "0.9rem" }} />
                            Unlock MLB Context · PS Plus
                          </a>
                        </Box>
                      </>
                    )}
                  </Box>
                </Paper>
              </Grid>
            )}
            {"common" in playerInfo && (
              <Grid item maxWidth="90vw">
                <EbayCards
                  isDesktop={props.isDesktop}
                  prod={props.prod}
                  playerInfoProp={playerInfo}
                  playerData={playerData}
                />
              </Grid>
            )}
          </Grid>
        </div>
      )}
    </div>
  );
}
